import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Brain, Sparkles, MessageCircle, Calendar, AlertTriangle, Phone,
  ClipboardList, Shield, CheckCircle2, ChevronDown, ChevronUp, RefreshCw,
  HelpCircle, ListChecks, DollarSign, FileText, Code2, Info,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRY_ICONS = {
  salon: '💅', real_estate: '🏠', general_construction: '🏗️',
  drywall: '🔨', plumbing: '🔧', hvac: '❄️', bathroom_remodeling: '🛁',
};

export function IndustryPreviewCard({ industry, compact = false }) {
  if (!industry) return null;
  const icon = INDUSTRY_ICONS[industry.id] || '🏢';

  return (
    <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4" data-testid="industry-preview-card">
      <div className="flex items-center gap-3">
        <span className="text-2xl">{icon}</span>
        <div>
          <h3 className="font-semibold text-foreground text-base" data-testid="industry-preview-name">
            Your AI is tuned for {industry.name}
          </h3>
          <p className="text-xs text-muted-foreground">{industry.assistant_role}</p>
        </div>
      </div>

      {industry.greeting_template && (
        <div className="rounded-lg bg-muted/40 p-3">
          <p className="text-xs text-muted-foreground mb-1 font-medium flex items-center gap-1"><MessageCircle size={12} /> Sample Greeting</p>
          <p className="text-sm text-foreground italic">"{industry.greeting_template}"</p>
        </div>
      )}

      {!compact && (
        <>
          {industry.qualification_questions?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1"><ClipboardList size={12} /> Questions your AI will ask</p>
              <ul className="space-y-1">
                {industry.qualification_questions.slice(0, 5).map((q, i) => (
                  <li key={i} className="text-sm text-foreground flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5 flex-shrink-0"><CheckCircle2 size={13} /></span>
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {industry.appointment_types?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1"><Calendar size={12} /> Appointment types</p>
              <div className="flex flex-wrap gap-1.5">
                {industry.appointment_types.slice(0, 6).map((t, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>
                ))}
              </div>
            </div>
          )}

          {industry.urgency_rules?.high?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1"><AlertTriangle size={12} /> Emergency triggers</p>
              <div className="flex flex-wrap gap-1.5">
                {industry.urgency_rules.high.slice(0, 5).map((t, i) => (
                  <Badge key={i} variant="destructive" className="text-xs font-normal">{t}</Badge>
                ))}
              </div>
            </div>
          )}

          {industry.lead_fields?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1"><Phone size={12} /> Lead info collected</p>
              <p className="text-sm text-foreground">{industry.lead_fields.join(', ')}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}


export function IndustrySampleScenarios({ scenarios }) {
  const [expanded, setExpanded] = useState(false);
  if (!scenarios?.length) return null;
  const shown = expanded ? scenarios : scenarios.slice(0, 2);

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Sparkles size={12} /> Sample call scenarios</p>
      {shown.map((s, i) => (
        <div key={i} className="rounded-lg border border-border/50 bg-muted/30 p-3">
          <p className="text-xs font-semibold text-foreground">{s.title}</p>
          <p className="text-xs text-muted-foreground mt-1 italic">"{s.caller}"</p>
          <p className="text-xs text-muted-foreground/70 mt-0.5">{s.context}</p>
        </div>
      ))}
      {scenarios.length > 2 && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-primary hover:text-primary/80 flex items-center gap-1">
          {expanded ? <><ChevronUp size={12} /> Show less</> : <><ChevronDown size={12} /> Show {scenarios.length - 2} more</>}
        </button>
      )}
    </div>
  );
}


export function IndustryBrainSection({ onIndustryChange }) {
  const { token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [currentIndustry, setCurrentIndustry] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [changing, setChanging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedNew, setSelectedNew] = useState('');
  const [expanded, setExpanded] = useState({});
  const hdrs = { Authorization: `Bearer ${token}` };

  const loadPreview = async (industryId) => {
    if (!industryId) return;
    try {
      const res = await axios.get(`${API}/industry/preview/${industryId}`);
      setPreview(res.data);
    } catch {
      setPreview(null);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [meRes, listRes] = await Promise.all([
          axios.get(`${API}/industry/me`, { headers: hdrs }),
          axios.get(`${API}/industry/list`),
        ]);
        setCurrentIndustry(meRes.data.resolved_industry);
        setProfile(meRes.data);
        setIndustries(listRes.data.industries || []);
        await loadPreview(meRes.data.resolved_industry);
      } catch { /* ignore */ }
    };
    load();
  }, [token]);

  const handleChange = async () => {
    if (!selectedNew || selectedNew === currentIndustry) return;
    setSaving(true);
    try {
      await axios.put(`${API}/industry/me`, { industry: selectedNew }, { headers: hdrs });
      // Reload
      const meRes = await axios.get(`${API}/industry/me`, { headers: hdrs });
      setCurrentIndustry(meRes.data.resolved_industry);
      setProfile(meRes.data);
      await loadPreview(meRes.data.resolved_industry);
      setChanging(false);
      setSelectedNew('');
      toast.success('Industry updated! Your AI assistant has been reconfigured.');
      if (onIndustryChange) onIndustryChange(selectedNew);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update industry');
    }
    setSaving(false);
  };

  if (!profile) return null;

  const fullProfile = profile.full_profile || {};
  const icon = INDUSTRY_ICONS[currentIndustry] || '🏢';

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="space-y-5" data-testid="industry-brain-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center text-xl">
            {icon}
          </div>
          <div>
            <h3 className="font-semibold text-foreground" data-testid="brain-industry-name">{preview?.display_name || fullProfile.name || profile.profile?.name || currentIndustry}</h3>
            <p className="text-xs text-muted-foreground">{preview?.assistant_role || fullProfile.assistant_role || profile.profile?.assistant_role || ''}</p>
          </div>
        </div>
        {!changing && (
          <Button variant="outline" size="sm" onClick={() => setChanging(true)} data-testid="change-industry-btn">
            <RefreshCw size={14} className="mr-1" /> Change Industry
          </Button>
        )}
      </div>

      {/* Preview disclaimer */}
      {preview?.preview_disclaimer && (
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 flex items-start gap-2" data-testid="preview-disclaimer">
          <Info size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-foreground/80">{preview.preview_disclaimer}</p>
        </div>
      )}

      {/* Change Industry Panel */}
      {changing && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-foreground">Changing your industry will update your AI assistant's default behavior, questions, and templates. Your custom company instructions will be preserved.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {industries.map(ind => (
                <button key={ind.id} onClick={() => setSelectedNew(ind.id)}
                  className={`text-left p-3 rounded-lg border text-sm transition-all ${
                    selectedNew === ind.id ? 'border-emerald-500 bg-emerald-500/10' :
                    ind.id === currentIndustry ? 'border-border bg-muted/30 opacity-50' : 'border-border hover:border-muted-foreground/30'
                  }`} data-testid={`change-industry-${ind.id}`}>
                  <span className="text-base mr-1">{INDUSTRY_ICONS[ind.id] || '🏢'}</span>
                  <span className="font-medium">{ind.name}</span>
                  {ind.id === currentIndustry && <Badge variant="secondary" className="ml-1 text-[10px]">Current</Badge>}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleChange} disabled={!selectedNew || selectedNew === currentIndustry || saving}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-change-industry-btn">
                {saving ? 'Updating...' : 'Confirm Change'}
              </Button>
              <Button variant="ghost" onClick={() => { setChanging(false); setSelectedNew(''); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI role summary */}
      {preview?.assistant_role && (
        <div className="rounded-lg border border-border/50 bg-muted/20 p-3" data-testid="brain-ai-role">
          <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1"><Brain size={12} /> AI Role</p>
          <p className="text-sm text-foreground">{preview.assistant_role}</p>
        </div>
      )}

      {/* Services it understands */}
      {preview?.common_services?.length > 0 && (
        <CollapsibleSection title="Services It Understands" icon={ListChecks}
          expanded={expanded.services} onToggle={() => toggle('services')}
          dataTestid="section-services">
          <div className="flex flex-wrap gap-1.5">
            {preview.common_services.map((s, i) => (
              <Badge key={i} variant="outline" className="text-xs font-normal">{s}</Badge>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* FAQs — common questions + safe answers */}
      {preview?.faqs?.length > 0 && (
        <CollapsibleSection title={`Common Questions (${preview.faqs.length})`} icon={HelpCircle}
          expanded={expanded.faqs} onToggle={() => toggle('faqs')}
          dataTestid="section-faqs">
          <div className="space-y-3">
            {preview.faqs.map((f, i) => (
              <div key={i} className="rounded-lg border border-border/30 bg-muted/20 p-3">
                <p className="text-sm font-medium text-foreground flex items-start gap-2">
                  <span className="text-blue-400 mt-0.5"><HelpCircle size={13} /></span>
                  {f.q}
                </p>
                <p className="text-xs text-muted-foreground mt-1.5 pl-5">{f.a}</p>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Intake flow (deep pack) */}
      {preview?.intake_flow?.length > 0 && (
        <CollapsibleSection title="Intake Questions" icon={ClipboardList}
          expanded={expanded.intake} onToggle={() => toggle('intake')}
          dataTestid="section-intake">
          <ol className="space-y-1.5 list-decimal list-inside">
            {preview.intake_flow.map((step, i) => (
              <li key={i} className="text-sm text-foreground">
                {step.label}
                {step.required && <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-400">required</span>}
                {step.options?.length > 0 && (
                  <span className="ml-2 text-xs text-muted-foreground">({step.options.slice(0, 4).join(' / ')}{step.options.length > 4 ? '…' : ''})</span>
                )}
              </li>
            ))}
          </ol>
        </CollapsibleSection>
      )}

      {/* Legacy qualification questions (only shown when no deep intake) */}
      {!preview?.intake_flow?.length && preview?.qualification_questions?.length > 0 && (
        <CollapsibleSection title="Qualification Questions" icon={ClipboardList}
          expanded={expanded.questions} onToggle={() => toggle('questions')}
          dataTestid="section-qualification">
          <ul className="space-y-1.5">
            {preview.qualification_questions.map((q, i) => (
              <li key={i} className="text-sm text-foreground flex items-start gap-2">
                <span className="text-emerald-500 mt-0.5"><CheckCircle2 size={13} /></span>{q}
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {/* Urgency & Escalation */}
      {(preview?.urgency_rules || preview?.escalation_rules?.length > 0) && (
        <CollapsibleSection title="Urgency & Escalation" icon={AlertTriangle}
          expanded={expanded.urgency} onToggle={() => toggle('urgency')}
          dataTestid="section-urgency">
          <div className="space-y-3">
            {preview.urgency_rules?.high?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-red-400 mb-1">High priority triggers</p>
                <div className="flex flex-wrap gap-1">
                  {preview.urgency_rules.high.map((t, i) => (
                    <Badge key={i} variant="destructive" className="text-xs font-normal">{t}</Badge>
                  ))}
                </div>
              </div>
            )}
            {preview.urgency_rules?.medium?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-amber-400 mb-1">Medium</p>
                <div className="flex flex-wrap gap-1">
                  {preview.urgency_rules.medium.map((t, i) => (
                    <Badge key={i} className="text-xs font-normal bg-amber-500/15 text-amber-400 border-0">{t}</Badge>
                  ))}
                </div>
              </div>
            )}
            {preview.escalation_rules?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-foreground mb-1.5">Escalation rules</p>
                <ul className="space-y-2">
                  {preview.escalation_rules.map((rule, i) => (
                    <li key={i} className="text-xs rounded-md border border-border/40 bg-muted/20 p-2">
                      <p className="text-foreground">When <span className="font-medium">{rule.when || rule.trigger || '—'}</span></p>
                      {rule.message && <p className="text-muted-foreground mt-1 italic">"{rule.message}"</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Appointment types */}
      {preview?.appointment_types?.length > 0 && (
        <CollapsibleSection title="Appointment Types" icon={Calendar}
          expanded={expanded.appointments} onToggle={() => toggle('appointments')}
          dataTestid="section-appointments">
          <div className="flex flex-wrap gap-1.5">
            {preview.appointment_types.map((t, i) => <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>)}
          </div>
        </CollapsibleSection>
      )}

      {/* Pricing rules */}
      {preview?.pricing_rules?.policy && (
        <CollapsibleSection title="Pricing Policy" icon={DollarSign}
          expanded={expanded.pricing} onToggle={() => toggle('pricing')}
          dataTestid="section-pricing">
          <p className="text-sm text-foreground">{preview.pricing_rules.policy}</p>
          {preview.pricing_rules.scripted_lines?.length > 0 && (
            <div className="mt-2 space-y-1">
              {preview.pricing_rules.scripted_lines.map((line, i) => (
                <p key={i} className="text-xs italic text-muted-foreground">"{line}"</p>
              ))}
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* SMS / Email templates */}
      {(Object.keys(preview?.sms_templates || {}).length > 0 || Object.keys(preview?.email_templates || {}).length > 0) && (
        <CollapsibleSection title="SMS & Email Templates" icon={MessageCircle}
          expanded={expanded.templates} onToggle={() => toggle('templates')}
          dataTestid="section-templates">
          <div className="space-y-2">
            {Object.entries(preview.sms_templates || {}).map(([name, body]) => (
              <div key={name} className="rounded-lg bg-muted/30 p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1 capitalize">SMS · {name}</p>
                <p className="text-xs text-foreground whitespace-pre-wrap">{body}</p>
              </div>
            ))}
            {Object.entries(preview.email_templates || {}).map(([name, body]) => (
              <div key={name} className="rounded-lg bg-muted/30 p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1 capitalize">Email · {name}</p>
                <p className="text-xs text-foreground whitespace-pre-wrap">{body}</p>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Lead fields */}
      {preview?.lead_fields?.length > 0 && (
        <CollapsibleSection title="Lead Info Captured" icon={Phone}
          expanded={expanded.lead} onToggle={() => toggle('lead')}
          dataTestid="section-lead-fields">
          <p className="text-sm text-foreground">{preview.lead_fields.join(', ')}</p>
        </CollapsibleSection>
      )}

      {/* Safety rules */}
      {preview?.safety_rules?.length > 0 && (
        <CollapsibleSection title="Safety Rules" icon={Shield}
          expanded={expanded.safety} onToggle={() => toggle('safety')}
          dataTestid="section-safety">
          <ul className="space-y-1.5">
            {preview.safety_rules.map((s, i) => (
              <li key={i} className="text-sm text-foreground flex items-start gap-2">
                <span className="text-rose-400 mt-0.5"><Shield size={13} /></span>
                {s}
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {/* Sample scenarios (legacy) */}
      {fullProfile.sample_scenarios?.length > 0 && (
        <CollapsibleSection title="Sample Call Scenarios" icon={Sparkles}
          expanded={expanded.scenarios} onToggle={() => toggle('scenarios')}
          dataTestid="section-scenarios">
          <IndustrySampleScenarios scenarios={fullProfile.sample_scenarios} />
        </CollapsibleSection>
      )}

      {/* Rendered prompt preview */}
      {preview?.rendered_prompt_preview && (
        <CollapsibleSection title="Rendered AI Prompt (preview)" icon={Code2}
          expanded={expanded.prompt} onToggle={() => toggle('prompt')}
          dataTestid="section-rendered-prompt">
          <pre className="text-[11px] leading-relaxed text-foreground/90 bg-muted/40 rounded-md p-3 overflow-x-auto whitespace-pre-wrap font-mono">
            {preview.rendered_prompt_preview}
          </pre>
        </CollapsibleSection>
      )}
    </div>
  );
}


function CollapsibleSection({ title, icon: Icon, expanded, onToggle, children, dataTestid }) {
  return (
    <div className="border border-border/50 rounded-lg overflow-hidden" data-testid={dataTestid}>
      <button onClick={onToggle} className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors" data-testid={dataTestid ? `${dataTestid}-toggle` : undefined}>
        <span className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Icon size={14} className="text-muted-foreground" />{title}
        </span>
        {expanded ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
      </button>
      {expanded && <div className="p-3 pt-0 border-t border-border/30">{children}</div>}
    </div>
  );
}
